import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, Input, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import * as Chart from 'chart.js';
import { ChartOption, BarChartOption, LineChartOption, PieChartOption, BarLineChartOption } from './chart-control.model';
import $ from 'jquery';
import * as uuid from 'uuid';

@Component({
  selector: 'sd-chart-control',
  templateUrl: './chart-control.component.html',
  styleUrls: ['../../assets/css/scrollbar.scss', './chart-control.component.css']
})
export class ChartControlComponent implements OnInit, AfterViewInit {
  private initialized = false;
  private pChartOption: ChartOption;
  @Input() set chartOption(chartOption: ChartOption) {
    this.pChartOption = chartOption;
    if (this.initialized) {
      this.renderChart();
    }
  }
  @ViewChild('chart') chartRef: ElementRef;
  @ViewChild('chartWrapper') chartWrapperRef: ElementRef;
  @ViewChild('chartAxis') chartAxisRef: ElementRef;
  @ViewChild('chartAreaWrapperExtend') chartAreaWrapperExtendRef: ElementRef;
  chart: Chart;
  visible = true;
  height = '300px';
  private ctx: CanvasRenderingContext2D;
  constructor(
    private ref: ChangeDetectorRef,
    public datePipe: DatePipe
  ) { }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.renderChart(true);
    this.initialized = true;
    this.ref.detectChanges();
  }

  private async renderChart(firstRender?: boolean) {
    this.height = this.pChartOption?.height || '300px';
    if (!firstRender) {
      this.visible = false;
      await new Promise(resolve => setTimeout(resolve, 500));
      this.visible = true;
    }
    setTimeout(() => {
      const canvas = this.chartRef.nativeElement as HTMLCanvasElement;
      this.ctx = canvas.getContext('2d');
      switch (this.pChartOption.type) {
        case 'pie':
          this.renderPieChart(this.pChartOption);
          break;
        case 'bar':
          this.renderBarChart(this.pChartOption);
          break;
        case 'line':
          this.renderLineChart(this.pChartOption);
          break;
        case 'barline':
          this.renderBarLineChart(this.pChartOption);
          break;
        default: break;
      }
    }, 500);
  }

  private renderPieChart(option: PieChartOption) {
  }

  private renderBarChart(option: BarChartOption) {
    const labelWidth = option.labelWidth ?? 50;
    if (option.datasets.length) {
      const newwidth = Math.max(option.labels.length * option.datasets.length * labelWidth, $(this.chartWrapperRef.nativeElement).width());
      $(this.chartAreaWrapperExtendRef.nativeElement).width(newwidth);
    }
    const configuration: Chart.ChartConfiguration = {
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: !option.hideLegend
        },
        scales: {
          xAxes: [{
            display: true,
            scaleLabel: {
              display: !!option.xTitle,
              labelString: option.xTitle || ''
            }
          }],
          yAxes: [{
            display: true,
            ticks: {
              beginAtZero: option.beginAtZero ?? true,
            },
            scaleLabel: {
              display: !!option.yTitle,
              labelString: option.yTitle || ''
            }
          }]
        }
      },
      type: 'bar',
      data: {
        labels: option.labels,
        datasets: option.datasets.map(e => {
          const result: Chart.ChartDataSets = {
            label: e.label,
            fill: e.backgroundColor,
            backgroundColor: e.backgroundColor,
            data: e.data
          };
          return result;
        })
      }
    };
    this.chart = new Chart(this.ctx, configuration);
  }

  private renderLineChart(option: LineChartOption) {
    const labelWidth = option.labelWidth ?? 50;
    if (option.datasets.length) {
      const newwidth = Math.max(option.labels.length * labelWidth, $(this.chartWrapperRef.nativeElement).width());
      $(this.chartAreaWrapperExtendRef.nativeElement).width(newwidth);
    }
    const configuration: Chart.ChartConfiguration = {
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: !option.hideLegend
        },
        scales: {
          xAxes: [{
            display: true,
            scaleLabel: {
              display: !!option.xTitle,
              labelString: option.xTitle || ''
            }
          }],
          yAxes: [{
            display: true,
            ticks: {
              beginAtZero: option.beginAtZero ?? true
            },
            scaleLabel: {
              display: !!option.yTitle,
              labelString: option.yTitle || ''
            }
          }]
        }
      },
      type: 'line',
      data: {
        labels: option.labels,
        datasets: option.datasets.map(e => {
          const result: Chart.ChartDataSets = {
            label: e.label,
            fill: e.backgroundColor,
            backgroundColor: e.backgroundColor,
            borderColor: e.borderColor,
            data: e.data,
            lineTension: 0,
          };
          return result;
        })
      }
    };
    this.chart = new Chart(this.ctx, configuration);
  }

  private renderBarLineChart(option: BarLineChartOption) {
    const labelWidth = option.labelWidth ?? 50;
    if (option.datasets.length) {
      const numberOfBars = option.datasets.filter(e => e.type === 'bar').length;
      if (numberOfBars <= 1) {
        const newwidth = Math.max(option.labels.length * labelWidth, $(this.chartWrapperRef.nativeElement).width());
        $(this.chartAreaWrapperExtendRef.nativeElement).width(newwidth);
      } else {
        const newwidth = Math.max(option.labels.length * numberOfBars * labelWidth, $(this.chartWrapperRef.nativeElement).width());
        $(this.chartAreaWrapperExtendRef.nativeElement).width(newwidth);
      }
    }
    const leftId = `I${uuid.v4()}`;
    const rightId = `I${uuid.v4()}`;
    const yAxes = [{
      type: 'linear',
      display: true,
      position: 'left',
      id: leftId,
      ticks: {
        beginAtZero: option.beginAtZero ?? true,
        suggestedMin: 0
      },
      scaleLabel: {
        display: !!option.yTitle,
        labelString: option.yTitle || ''
      }
    }];
    if (option.datasets.some(e => e.yAlign === 'right')) {
      yAxes.push({
        type: 'linear',
        display: true,
        position: 'right',
        id: rightId,
        ticks: {
          beginAtZero: option.beginAtZero ?? true,
          suggestedMin: 0
        },
        scaleLabel: {
          display: !!option.yRightTitle,
          labelString: option.yRightTitle || ''
        }
      });
    }
    const configuration: Chart.ChartConfiguration = {
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: !option.hideLegend
        },
        title: {
          display: true,
          text: option.title
        },
        scales: {
          xAxes: [{
            display: true,
            scaleLabel: {
              display: !!option.xTitle,
              labelString: option.xTitle || ''
            }
          }],
          yAxes
        }
      },
      data: {
        labels: option.labels,
        datasets: option.datasets.map(e => {
          const result: Chart.ChartDataSets = {
            type: e.type,
            label: e.label,
            fill: e.backgroundColor,
            backgroundColor: e.backgroundColor,
            borderColor: e.borderColor,
            data: e.data,
            yAxisID: e.yAlign === 'right' ? rightId : leftId,
            lineTension: 0
          };
          return result;
        })
      }
    };
    this.chart = new Chart(this.ctx, configuration);
  }
}
